<template>
  <div v-if="statusData" class="sms-status h-4 w-4 flex-none">
    <icon-base
      v-tooltip="statusData.tooltip"
      :icon="statusData.icon"
      :class="statusData.color"
      :view-box="statusData.viewBox ?? '0 0 16 16'"
    />
  </div>
</template>

<script setup lang="ts">
import { computed } from "vue";
import { useI18n } from "vue-i18n";

import { SmsStatus } from "@/enums/sms";

import IconLoadingCircle from "@/components/icons/IconLoadingCircle.vue";
import IconBounced from "@/components/icons/email/IconBounced.vue";
import IconDelivered from "@/components/icons/email/IconDelivered.vue";
import IconSent from "@/components/icons/email/IconSent.vue";
import IconBase from "@/components/ui/IconBase.vue";

interface Props {
  status: SmsStatus;
  statusMessage?: string | null;
}

const { status, statusMessage } = defineProps<Props>();

const { t } = useI18n();

const statusData = computed(() => {
  if (status === SmsStatus.Sending) {
    return {
      icon: IconLoadingCircle,
      tooltip: t("ACTIVITY.SMS.STATUS.SENDING"),
      color: "text-primary",
      viewBox: "0 0 20 20",
    };
  }

  if (status === SmsStatus.Sent) {
    return {
      icon: IconSent,
      tooltip: t("ACTIVITY.SMS.STATUS.SENT"),
      color: "text-gray-500",
    };
  }

  if (status === SmsStatus.Delivered) {
    return {
      icon: IconDelivered,
      tooltip: t("ACTIVITY.SMS.STATUS.DELIVERED"),
      color: "text-blue-500",
    };
  }

  if (status === SmsStatus.Undelivered) {
    return {
      icon: IconBounced,
      tooltip: statusMessage ?? t("ACTIVITY.SMS.STATUS.UNDELIVERED"),
      color: "text-error",
    };
  }

  if (
    [
      SmsStatus.Failed,
      SmsStatus.FailedRetry,
      SmsStatus.ValidationFailed,
    ].includes(status)
  ) {
    return {
      icon: IconBounced,
      tooltip: statusMessage || t("ACTIVITY.SMS.STATUS.FAILED"),
      color: "text-error",
    };
  }

  return null;
});
</script>
